import {
  ArrowDownLeft,
  ArrowUpRight,
  Paperclip,
  Reply,
  UserPlus,
} from "lucide-react";
import { useGetIdentity, useLocaleState, useTranslate } from "ra-core";
import { useState } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { formatRelativeDate } from "../misc/RelativeDate";
import type { Contact, Email, EmailRecipient } from "../types";
import { contactDraftFromSender } from "./contactDraft";
import type { EmailDraft } from "./ComposeEmailDialog";
import { replySubject } from "./emailTemplates";

const SNIPPET_LENGTH = 180;

export const recipientNames = (recipients: EmailRecipient[]) =>
  recipients.map((recipient) => recipient.name || recipient.email).join(", ");

/** Draft of a reply: to the sender of a received email, to the recipients of a sent one. */
export const replyDraft = (email: Email): EmailDraft => ({
  to:
    email.direction === "in" && email.from_email
      ? [{ email: email.from_email, name: email.from_name ?? undefined }]
      : email.to_recipients,
  subject: replySubject(email.subject),
  links: {
    contact_ids: email.contact_ids,
    company_ids: email.company_ids,
    partner_ids: email.partner_ids,
    deal_ids: email.deal_ids,
  },
  in_reply_to: email.message_id ?? undefined,
  references: [
    ...(email.thread_references ?? []),
    ...(email.message_id ? [email.message_id] : []),
  ],
});

/**
 * A received email that only matched a company domain: the sender is not a
 * contact yet. Its name (or its address) prefills the new contact.
 */
export const contactDraftFromEmail = (email: Email): Partial<Contact> =>
  contactDraftFromSender(
    { email: email.from_email ?? "", name: email.from_name ?? undefined },
    email.company_ids[0],
  );

const isFromUnknownSender = (email: Email) =>
  email.direction === "in" &&
  !!email.from_email &&
  email.contact_ids.length === 0 &&
  email.partner_ids.length === 0;

/** The message itself: plain text, or the HTML in a frame that runs nothing. */
const EmailBody = ({ email }: { email: Email }) =>
  email.body_text ? (
    <div className="whitespace-pre-line text-sm break-words">
      {email.body_text}
    </div>
  ) : email.body_html ? (
    <iframe
      title={email.subject ?? "email"}
      sandbox=""
      srcDoc={email.body_html}
      className="h-96 w-full rounded-md border bg-white"
    />
  ) : null;

/** One email of a feed: who, when and subject; a click shows the whole message. */
export const EmailItem = ({
  email,
  onReply,
}: {
  email: Email;
  onReply?: (draft: EmailDraft) => void;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const { identity } = useGetIdentity();
  const [isOpen, setOpen] = useState(false);
  const isSent = email.direction === "out";
  const sender =
    isSent && email.sales_id === identity?.id
      ? translate("crm.mail.emails.you")
      : email.from_name || email.from_email || "";
  const snippet = (email.body_text ?? "").replace(/\s+/g, " ").trim();

  return (
    <div className="flex flex-col gap-2 text-sm">
      <button
        type="button"
        onClick={() => setOpen(!isOpen)}
        aria-expanded={isOpen}
        className="flex flex-col gap-1 text-left cursor-pointer rounded-sm hover:bg-muted/50"
      >
        <span className="flex items-center gap-2 text-muted-foreground">
          {isSent ? (
            <ArrowUpRight className="size-4 shrink-0 text-blue-600" />
          ) : (
            <ArrowDownLeft className="size-4 shrink-0 text-emerald-600" />
          )}
          <span className="min-w-0 truncate">
            {translate(
              isSent
                ? "crm.mail.emails.sent_by"
                : "crm.mail.emails.received_from",
              { name: sender, recipients: recipientNames(email.to_recipients) },
            )}
          </span>
          <span className="ml-auto shrink-0 text-xs">
            {formatRelativeDate(email.sent_at, locale)}
          </span>
        </span>
        <span className="font-medium">
          {email.subject || translate("crm.mail.emails.no_subject")}
        </span>
        {!isOpen && snippet ? (
          <span className="line-clamp-2 text-muted-foreground">
            {snippet.slice(0, SNIPPET_LENGTH)}
          </span>
        ) : null}
      </button>
      {isOpen ? (
        <div className={cn("flex flex-col gap-3 border-l-2 pl-3")}>
          {email.cc_recipients?.length ? (
            <span className="text-xs text-muted-foreground">
              {translate("crm.mail.emails.cc", {
                recipients: recipientNames(email.cc_recipients),
              })}
            </span>
          ) : null}
          <EmailBody email={email} />
          {email.attachments?.length ? (
            <ul className="flex flex-wrap gap-2">
              {email.attachments.map((file, index) => (
                <li key={`${file.path ?? file.src ?? file.title}-${index}`}>
                  {file.src ? (
                    <a
                      href={file.src}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 rounded-md border px-2 py-1 hover:bg-muted"
                    >
                      <Paperclip className="size-3.5 text-muted-foreground" />
                      {file.title}
                    </a>
                  ) : (
                    // Received files stay in the mailbox.
                    <span
                      className="flex items-center gap-1 rounded-md border px-2 py-1 text-muted-foreground"
                      title={translate("crm.mail.emails.attachment_in_mailbox")}
                    >
                      <Paperclip className="size-3.5" />
                      {file.title}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="flex flex-wrap gap-2">
            {onReply ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onReply(replyDraft(email))}
              >
                <Reply className="size-4" />
                {translate("crm.mail.emails.reply")}
              </Button>
            ) : null}
            {isFromUnknownSender(email) ? (
              <Button asChild variant="outline" size="sm">
                <Link
                  to="/contacts/create"
                  state={{ record: contactDraftFromEmail(email) }}
                >
                  <UserPlus className="size-4" />
                  {translate("crm.mail.emails.create_contact")}
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};
